// src/context/UnidadeContext.tsx

import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { db } from "services/firebase";
import { collection, addDoc, getDocs, onSnapshot, doc, updateDoc, deleteDoc, query, where } from "firebase/firestore";
import { useAuth } from "./AuthContext";

export interface Unidade {
  id: string;
  nome: string;
  dispositivoId: string;
  data: string;
  contagemAtual: number;
  metaMaxima: number | null;
}

interface UnidadeContextType {
  unidades: Unidade[];
  adicionarUnidade: (u: Omit<Unidade, "id">) => Promise<void>;
  atualizarUnidade: (id: string, dados: Partial<Unidade>) => Promise<void>;
  removerUnidade: (deviceId: string) => Promise<void>;
}

const UnidadeContext = createContext<UnidadeContextType | undefined>(undefined);

export const UnidadeProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [unidades, setUnidades] = useState<Unidade[]>([]);

  useEffect(() => {
    if (!user?.uid) {
      setUnidades([]);
      return;
    }

    const collectionPath = `clientes/${user.uid}/unidades`;
    const q = query(collection(db, collectionPath));

    const unsub = onSnapshot(q, (snapshot) => {
      const arr: Unidade[] = snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as Omit<Unidade, "id">) }));
      setUnidades(arr);
    });

    return () => unsub();
  }, [user]);

  const getCollectionRef = () => {
    if (!user?.uid) throw new Error("Usuário não autenticado.");
    return collection(db, `clientes/${user.uid}/unidades`);
  };

  const getDocRef = (id: string) => {
    if (!user?.uid) throw new Error("Usuário não autenticado.");
    return doc(db, `clientes/${user.uid}/unidades`, id);
  };

  const adicionarUnidade = async (u: Omit<Unidade, "id">) => {
    await addDoc(getCollectionRef(), u);
  };

  const atualizarUnidade = async (id: string, dados: Partial<Unidade>) => {
    await updateDoc(getDocRef(id), dados);
  };

  const removerUnidade = async (deviceId: string) => {
    try {
      const q = query(getCollectionRef(), where("dispositivoId", "==", deviceId));
      const snapshot = await getDocs(q);
      
      const deletePromises = snapshot.docs.map(docSnapshot => 
        deleteDoc(doc(getCollectionRef().firestore, getCollectionRef().path, docSnapshot.id))
      );
      
      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Erro ao remover unidade por dispositivo:", error);
    }
  };

  return (
    <UnidadeContext.Provider value={{ unidades, adicionarUnidade, atualizarUnidade, removerUnidade }}>
      {children}
    </UnidadeContext.Provider>
  );
};

export const useUnidades = () => {
  const ctx = useContext(UnidadeContext);
  if (!ctx) throw new Error("useUnidades must be inside UnidadeProvider");
  return ctx;
};
